import { v4 as uuidv4 } from 'uuid';
import { awsDateFormatter } from 'core/helpers/formatters';
import { usesBrandInterestedPartyTypes } from 'common/helpers/auto-interested-party-helpers';
import { rapaCoverageSymbols } from 'common/helpers/car-symbols-helpers';

/**
 * Builds the car object and optional additionalParty (lienholder) from the AddCar form values.
 * Separates lienholder fields from car data so the car object stays clean and the
 * lienholder can be added to the policy's additionalParties separately.
 *
 * @param {Object} params
 * @param {Object} params.values - The AddCar form values (VIN, coverages, lienholder fields)
 * @param {Object} params.garageLocation - The garage location to copy from the first existing car
 * @returns {{ car: Object, additionalParty: Object|null }}
 */
export const buildAddCarSubmitValues = ({ values, garageLocation }) => {
  const {
    carVIN,
    addLienholder,
    lienholderName,
    lienholderAddress,
    lienholderAddress2,
    lienholderCity,
    lienholderState,
    lienholderZip,
    lienholderLoanNumber,
    ...carFields
  } = values;

  const VIN = carVIN.toUpperCase();

  const car = {
    ...carFields,
    VIN,
    purchaseDate: awsDateFormatter(new Date()),
    garageLocation
  };

  let additionalParty = null;
  if (addLienholder && lienholderName) {
    additionalParty = {
      id: uuidv4(),
      name: lienholderName,
      loanNumber: lienholderLoanNumber || '',
      address: {
        address: lienholderAddress || '',
        address2: lienholderAddress2 || '',
        city: lienholderCity || '',
        state: lienholderState || '',
        zip: lienholderZip || ''
      },
      VIN,
      lossPayee: true,
      // #36264: a brand with typed interests records the lender as a Loss Payee on this vehicle.
      ...(usesBrandInterestedPartyTypes()
        ? { relationship: 'lossPayee', VINs: [VIN], trusteeNames: '' }
        : { relationship: 'lienholder' })
    };
  }

  return { car, additionalParty };
};

const getDeductibleValueFromId = (id) => {
  const idParts = id.split('/');
  return +idParts[0];
};

export const getDeductibleOptions = (options, state, currentDeductible, minimumDeductible) => {
  if (state === 'MD') {
    return options;
  }

  const currentValue = currentDeductible ? getDeductibleValueFromId(currentDeductible) : null;
  const minimum = currentValue && currentValue < minimumDeductible ? currentValue : minimumDeductible;

  return options.map((option) => {
    const value = getDeductibleValueFromId(option.id);
    return {
      ...option,
      disabled: +value < minimum || option.disabled
    };
  });
};

function parseErrorMetadata(error) {
  try {
    return JSON.parse(error.data);
  } catch (e) {
    console.log('error parsing error metadata', e);
    return null;
  }
}

const getCarNotificationFromErrorsAndRejections = (car, errorsAndRejections) => {
  const errorsAndRejectionsAndParsedMetadata = errorsAndRejections?.map((e) => ({
    ...e,
    data: parseErrorMetadata(e) || e.data
  }));

  if (
    errorsAndRejectionsAndParsedMetadata.some(
      (e) =>
        e?.data?.VINs?.includes(car.VIN) ||
        e?.data?.cars?.some(
          (carErrorMetadata) =>
            typeof carErrorMetadata === 'string' &&
            carErrorMetadata.includes(car.make) &&
            carErrorMetadata.includes(car.model) &&
            carErrorMetadata.includes(car.year)
        )
    )
  ) {
    return `This vehicle is ineligible due to Branch's underwriting rules, see rejection information for more details`;
  }

  return null;
};

// On a RAPA brand (CFM) the rater keeps a VIN Verisk has no record for — fleet, exotic, motorhome,
// brand-new, or simply mistyped — as a real car: year decoded from the VIN itself, no make/model, no
// symbols, rated on the filed default until an underwriter enters symbols by hand (#35274, #35585).
// Year-but-nothing-else is that no-hit; a car still waiting on the rater has no year yet. An antique
// is excluded: it rates on its stated amount with the 'A' symbol whatever Verisk returned. #35721
const isRapaNoHit = (car) =>
  !!car.VIN &&
  !!car.year &&
  !car.isAntique &&
  !car.make &&
  !car.model &&
  !car.rapaLiabilitySymbol1 &&
  !car.rapaLiabilitySymbol2 &&
  !car.rapaPhysDamSymbol1 &&
  !car.rapaPhysDamSymbol2 &&
  // Per-coverage symbols (#36504) — what an underwriter enters by hand now.
  !rapaCoverageSymbols.some(({ field }) => car[field]);

export const getCarNotification = (car, errorsAndRejections, routesContext, { usesRapaSymbols = false } = {}) => {
  const { inOfferEditRoute } = routesContext;

  const rejectionNotification = getCarNotificationFromErrorsAndRejections(car, errorsAndRejections);
  if (rejectionNotification) {
    return rejectionNotification;
  }
  if (car.hasOutstandingRecall) {
    return 'This make/model has an outstanding recall. We require proof within 15 days that this has been resolved to keep this vehicle on the policy.';
  }
  if (inOfferEditRoute && car.VIN?.endsWith('111111')) {
    return `This vehicle needs to be replaced with the vehicle's real VIN. If you have the real VIN, remove this vehicle and add it back with the real VIN to continue to checkout`;
  }
  if (usesRapaSymbols && isRapaNoHit(car)) {
    return `Verisk has no record for VIN ${car.VIN} — double-check the VIN. If it is correct, this vehicle is rated on the default symbol until an underwriter enters its year, make, model and rating symbols under Additional Car Details.`;
  }
};
